<script setup lang="ts">
import { computed } from 'vue'
import { useHead } from '@unhead/vue'
import type { Resolved } from '@/types'
import { settings, PATHS } from '@/content'
import { baseHead, breadcrumbLd } from '@/lib/seo'
import ContactForm from '@/components/ContactForm.vue'
import BreadcrumbBar from '@/components/BreadcrumbBar.vue'

const props = defineProps<{ resolved: Resolved }>()
const service = computed(() => props.resolved.service)

const crumbs = [
  { label: 'Accueil', path: '/' },
  { label: 'Contact', path: PATHS.contact },
]

useHead(() => ({
  ...baseHead({
    title: service.value?.seo?.seo_title || 'Contact - Les Ateliers du Terras (Mayenne, Laval, La Baule)',
    description:
      service.value?.seo?.seo_description ||
      'Contactez les Ateliers du Terras pour votre projet de menuiserie, agencement ou dépannage. Devis et étude gratuits à Mayenne, Laval et La Baule.',
    canonical: PATHS.contact,
  }),
  script: [breadcrumbLd(crumbs)],
}))
</script>

<template>
  <div class="contact">
    <BreadcrumbBar :crumbs="crumbs" />
    <section class="section">
      <div class="container contact__grid">
        <aside class="contact__aside">
          <h1>Vous avez un projet ou besoin d'un conseil ?</h1>
          <p class="contact__intro">
            Notre équipe sera ravie de vous rappeler pour répondre à vos demandes.
          </p>

          <div class="contact__block">
            <h2>Nos coordonnées</h2>
            <div v-for="a in settings.agencies" :key="a.city" class="contact__agency">
              <h3>{{ a.name }} - {{ a.city }}</h3>
              <address>
                {{ a.street }}<br />
                {{ a.zip }} {{ a.town }}<br />
                <a :href="`tel:${a.phone_tel}`">Tél : {{ a.phone_display }}</a>
              </address>
              <p class="contact__hours">{{ a.hours }}</p>
            </div>
            <p class="contact__email">
              <a :href="`mailto:${settings.email}`">{{ settings.email }}</a>
            </p>
            <p class="contact__rating">
              <span class="star">★</span> Note Google {{ settings.rating.value }}/5
              ({{ settings.rating.count }} avis)
            </p>
          </div>

          <ul class="contact__labels">
            <li v-for="l in settings.labels" :key="l">{{ l }}</li>
          </ul>
        </aside>

        <div class="contact__form">
          <h2>Remplissez le formulaire</h2>
          <ContactForm :service-path="PATHS.contact" />
        </div>
      </div>
    </section>

    <section class="section section--alt">
      <div class="container">
        <h2>Nous trouver</h2>
        <div class="contact__maps">
          <a
            v-for="a in settings.agencies"
            :key="a.city"
            :href="a.maps"
            target="_blank"
            rel="noopener"
            class="contact__map"
          >
            <strong>{{ a.city }}</strong>
            <span>{{ a.street }}, {{ a.zip }} {{ a.town }}</span>
            <span class="contact__map__link">Ouvrir dans Google Maps →</span>
          </a>
        </div>
      </div>
    </section>
  </div>
</template>

<style scoped>
.contact__grid {
  display: grid;
  grid-template-columns: 1fr 1.2fr;
  gap: 48px;
  align-items: start;
}
.contact__aside h1 {
  color: var(--green-dark);
  font-size: clamp(1.5rem, 3vw, 2rem);
}
.contact__intro {
  color: var(--ink);
}
.contact__block {
  margin-top: 20px;
}
.contact__block h2 {
  font-size: 1.15rem;
  border-bottom: 2px solid var(--green);
  display: inline-block;
  padding-bottom: 6px;
}
.contact__agency {
  margin: 16px 0;
}
.contact__agency h3 {
  color: var(--green-dark);
  font-size: 1rem;
  margin-bottom: 4px;
}
.contact__agency address {
  font-style: normal;
  line-height: 1.7;
}
.contact__agency address a {
  color: var(--title);
  font-weight: 700;
}
.contact__hours {
  color: var(--muted);
  font-size: 0.85rem;
  margin: 4px 0 0;
}
.contact__email a {
  font-weight: 700;
}
.contact__rating {
  font-weight: 600;
  font-size: 0.9rem;
}
.star {
  color: var(--green);
}
.contact__labels {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  list-style: none;
  padding: 0;
  margin: 20px 0 0;
}
.contact__labels li {
  border: 2px solid var(--green);
  color: var(--green-dark);
  font-weight: 800;
  text-transform: uppercase;
  font-size: 0.8rem;
  padding: 6px 14px;
  border-radius: var(--radius-sm);
}
.contact__form {
  background: #fff;
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
  padding: 28px;
}
.contact__form h2 {
  margin-top: 0;
  font-size: 1.2rem;
}
.contact__maps {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 16px;
}
.contact__map {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 22px;
  background: #fff;
  border: 1px solid var(--border);
  border-left: 3px solid var(--green);
  border-radius: var(--radius);
  color: var(--ink);
}
.contact__map:hover {
  text-decoration: none;
  box-shadow: var(--shadow-sm);
}
.contact__map strong {
  color: var(--green-dark);
  font-size: 1.1rem;
}
.contact__map__link {
  color: var(--green-dark);
  font-weight: 700;
  font-size: 0.9rem;
}
@media (max-width: 860px) {
  .contact__grid {
    grid-template-columns: 1fr;
    gap: 28px;
  }
  .contact__maps {
    grid-template-columns: 1fr;
  }
}
</style>
